import MapViz from './MapViz';
import GeoJsonComponent from './GeoJsonComponent';
import MapGradientLegend from './MapGradientLegend';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import ToggleButton from 'react-bootstrap/ToggleButton';
import Dropdown from 'react-bootstrap/Dropdown';
import { useState, useEffect, useId } from 'react';
import {
  fetchGeoJson,
  fetchCollectionMetadata,
} from '../apiClient/fetchGeoJson';
import { useSnackbar } from 'notistack';
import mapboxgl from 'mapbox-gl';
import type { FeatureCollection } from 'geojson';
import { useAppContext } from '../utils/AppContext';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';

export interface MapContainerProps {
  selectedRows?: string[];
  campaignId?: string;
  selectedNetworkType: string;
  center: [number, number];
  zoom: number;
  width: string;
  height: string;
  isEmbedded?: boolean;
  technology?: 'lte' | 'nr' | '2.4' | '5' | '6';
  band?: string;
  statistic?: 'max' | 'mean';
  binSize?: string;
  operator?: string;
  geopolygon?: any;
  baseLayer?: 'light' | 'satellite';
  showOperatorControl?: boolean;
  showTechnologyControl?: boolean;
  showBandControl?: boolean;
  showStatisticControl?: boolean;
  showBinSizeControl?: boolean;
  preventInitialFit?: boolean;
  tilt?: number;
  bearing?: number;
  showMap?: boolean;
}

export default function MapContainer({
  selectedRows,
  campaignId,
  selectedNetworkType,
  center,
  zoom,
  width,
  height,
  isEmbedded = false,
  technology: initialTechnology,
  band: initialBand,
  statistic: initialStatistic,
  binSize: initialBinSize,
  operator: initialOperator,
  geopolygon: geopolygonFromProps,
  baseLayer: initialBaseLayer = 'light',
  showOperatorControl = true,
  showTechnologyControl = true,
  showBandControl = true,
  showStatisticControl = true,
  showBinSizeControl = true,
  preventInitialFit = false,
  tilt,
  bearing,
  showMap = true,
}: MapContainerProps) {
  const [technology, setTechnology] = useState<
    'lte' | 'nr' | '2.4' | '5' | '6'
  >(initialTechnology || (selectedNetworkType === 'wifi' ? '6' : 'lte'));
  const [band, setBand] = useState<string>(initialBand || 'all');
  const [statistic, setStatistic] = useState<'max' | 'mean'>(
    initialStatistic || 'max',
  );
  const [geoJsonData, setGeoJsonData] = useState<FeatureCollection | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [operators, setOperators] = useState<string[]>([]);
  const [selectedOperator, setSelectedOperator] = useState<string>(
    initialOperator || 'all',
  );
  // lte and nr bands
  const [lteBands, setLteBands] = useState<string[]>([]);
  const [nrBands, setNrBands] = useState<string[]>([]);
  const [availableUniBands, setAvailableUniBands] = useState<string[]>([]);
  const [binSize, setBinSize] = useState<string>(
    initialBinSize || (selectedNetworkType === 'wifi' ? '5' : '100'),
  );
  const [shouldFitBounds, setShouldFitBounds] = useState(!preventInitialFit);
  const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null);
  const { geoPolygon: geoPolygonFromContext } = useAppContext();
  const [geoPolygon, setGeoPolygon] = useState(
    geopolygonFromProps || geoPolygonFromContext,
  );
  const [showBoundingBox, setShowBoundingBox] = useState(true);
  const [showGeoPolygon, setShowGeoPolygon] = useState(true);
  const [geoPolygonFilterEnabled, setGeoPolygonFilterEnabled] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [baseLayer, setBaseLayer] = useState<'light' | 'satellite'>(
    initialBaseLayer,
  );
  const [totalCaptures, setTotalCaptures] = useState<number | null>(null);
  const uniqueId = useId();

  const showAnyControl =
    (showOperatorControl && selectedNetworkType !== 'wifi') ||
    showTechnologyControl ||
    showBandControl ||
    showStatisticControl ||
    showBinSizeControl;

  // available bin sizes
  const avaiableBinSize = ['1', '5', '25', '100', '250', '500', '1000'];

  useEffect(() => {
    if (!preventInitialFit) {
      setShouldFitBounds(true);
    }
  }, [selectedRows, campaignId, preventInitialFit]);

  useEffect(() => {
    const loadMetadata = async () => {
      if (
        showMap &&
        ((selectedRows && selectedRows.length > 0) || campaignId)
      ) {
        try {
          const metadata = await fetchCollectionMetadata({
            selectedRows,
            campaignId,
          });
          setTotalCaptures(metadata.total_captures || null);
          if (selectedNetworkType !== 'wifi') {
            setOperators(metadata.operators);
            setLteBands(metadata.lte_bands);
            setNrBands(metadata.nr_bands);
          } else if (metadata.unii_bands) {
            setAvailableUniBands(metadata.unii_bands);
          }
        } catch (err) {
          setError(
            err instanceof Error ? err.message : 'Failed to load metadata',
          );
        }
      }
    };

    loadMetadata();
  }, [showMap, selectedNetworkType, selectedRows, campaignId]);

  // Reset band selection when technology changes
  useEffect(() => {
    setBand('all');
  }, [technology]);

  // Initial load when component mounts or selectedRows/campaignId changes
  useEffect(() => {
    const loadInitialGeoJson = async () => {
      if ((!selectedRows || !selectedRows.length) && !campaignId) return;

      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchGeoJson({
          selectedRows,
          campaignId,
          selectedNetworkType,
          band: formatBandValue(band, technology),
          technology,
          statistic,
          operator: selectedOperator === 'all' ? undefined : selectedOperator,
          binSize,
          geopolygon:
            geoPolygon && geoPolygonFilterEnabled
              ? JSON.stringify(geoPolygon)
              : undefined,
        });
        setGeoJsonData(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load map data',
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialGeoJson();
  }, [selectedRows, campaignId]);

  // Load GeoJSON data when filters are submitted
  useEffect(() => {
    const loadGeoJson = async () => {
      if (
        ((!selectedRows || !selectedRows.length) && !campaignId) ||
        !isSubmitted
      )
        return;

      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchGeoJson({
          selectedRows,
          campaignId,
          selectedNetworkType,
          band: formatBandValue(band, technology),
          technology,
          statistic,
          operator: selectedOperator === 'all' ? undefined : selectedOperator,
          binSize,
          geopolygon:
            geoPolygon && geoPolygonFilterEnabled
              ? JSON.stringify(geoPolygon)
              : undefined,
        });
        setGeoJsonData(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load map data',
        );
      } finally {
        setIsLoading(false);
        setIsSubmitted(false); // Reset submitted state after loading
      }
    };

    loadGeoJson();
  }, [isSubmitted]);

  const getBandOptions = () => {
    if (selectedNetworkType === 'wifi') {
      // Filter U-NII bands based on selected WiFi technology
      if (technology === '2.4') {
        // 2.4 GHz bands: 2.4GHz
        const twoFourGhzBands = availableUniBands.filter((band) =>
          ['2.4GHz'].includes(band),
        );
        return ['all', ...twoFourGhzBands];
      } else if (technology === '5') {
        // 5 GHz bands: U-NII-1, U-NII-2A, U-NII-2B, U-NII-2C, U-NII-3, U-NII-4
        const fiveGhzBands = availableUniBands.filter((band) =>
          [
            'U-NII-1',
            'U-NII-2A',
            'U-NII-2B',
            'U-NII-2C',
            'U-NII-3',
            'U-NII-4',
          ].includes(band),
        );
        return ['all', ...fiveGhzBands];
      } else if (technology === '6') {
        // 6 GHz bands: U-NII-5, U-NII-6, U-NII-7, U-NII-8
        const sixGhzBands = availableUniBands.filter((band) =>
          ['U-NII-5', 'U-NII-6', 'U-NII-7', 'U-NII-8'].includes(band),
        );
        return ['all', ...sixGhzBands];
      }
      return ['all', ...availableUniBands];
    }
    // For cellular, show bands based on selected technology
    if (technology === 'lte') {
      return ['all', ...lteBands];
    } else if (technology === 'nr') {
      return ['all', ...nrBands];
    }
    return ['all'];
  };

  const getTechnologyOptions = () => {
    if (selectedNetworkType === 'wifi') {
      return [
        { value: '2.4', label: '2.4 GHz' },
        { value: '5', label: '5 GHz' },
        { value: '6', label: '6 GHz' },
      ];
    }
    return [
      { value: 'lte', label: 'LTE' },
      { value: 'nr', label: 'NR' },
    ];
  };

  // Helper function to format band value based on technology
  const formatBandValue = (bandValue: string, tech: string) => {
    if (bandValue === 'all') return 'all';
    if (tech === 'nr' && bandValue !== 'all') {
      return `n${bandValue}`;
    }
    return bandValue;
  };

  const handleShare = () => {
    const params = new URLSearchParams();
    if (selectedRows) {
      params.set('collection_ids', selectedRows.join(','));
    } else if (campaignId) {
      params.set('campaign_id', campaignId);
    }

    params.set('network_type', selectedNetworkType);
    params.set('technology', technology);
    params.set('band', band);
    params.set('statistic', statistic);
    params.set('bin_size', binSize);

    if (selectedOperator !== 'all') {
      params.set('operator', selectedOperator);
    }
    if (geoPolygon && geoPolygonFilterEnabled) {
      params.set('geopolygon', JSON.stringify(geoPolygon));
    }
    params.set('base_layer', baseLayer);

    if (mapInstance) {
      const center = mapInstance.getCenter();
      params.set('center', `${center.lat},${center.lng}`);
      params.set('zoom', mapInstance.getZoom().toString());
      params.set('tilt', mapInstance.getPitch().toString());
      params.set('bearing', mapInstance.getBearing().toString());
    }

    const shareUrl = `${window.location.origin}/share?${params.toString()}`;
    navigator.clipboard.writeText(shareUrl).then(
      () => {
        enqueueSnackbar('Share link copied to clipboard!', {
          variant: 'success',
        });
      },
      (err) => {
        enqueueSnackbar('Failed to copy share link.', { variant: 'error' });
        console.error('Could not copy text: ', err);
      },
    );
  };

  const handleSubmit = () => {
    setIsSubmitted(true);
    setShouldFitBounds(false);
  };

  return (
    <div style={{ width, height, display: 'flex', flexDirection: 'column' }}>
      {error && <div className="alert alert-danger">{error}</div>}
      <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 0 }}>
        <MapViz
          height="100%"
          width="100%"
          zoom={zoom}
          center={center}
          tilt={tilt}
          bearing={bearing}
          selectedNetworkType={selectedNetworkType}
          isLoading={isLoading}
          onMapLoad={setMapInstance}
          baseLayer={baseLayer}
          onBaseLayerChange={setBaseLayer}
        >
          {(map) => (
            <>
              <GeoJsonComponent
                geoJsonData={geoJsonData}
                selectedNetworkType={selectedNetworkType}
                map={map}
                shouldFitBounds={shouldFitBounds}
                showBoundingBox={showBoundingBox}
                showGeoPolygon={showGeoPolygon}
              />
              <MapGradientLegend selectedNetworkType={selectedNetworkType} />
            </>
          )}
        </MapViz>
        <button
          onClick={() => {
            if (mapInstance && geoJsonData?.bbox) {
              const bounds = new mapboxgl.LngLatBounds(
                [geoJsonData.bbox[0], geoJsonData.bbox[1]],
                [geoJsonData.bbox[2], geoJsonData.bbox[3]],
              );
              mapInstance.fitBounds(bounds, {
                padding: 50,
                duration: 1000, // Animate the transition
              });
            }
          }}
          style={{
            position: 'absolute',
            top: '10px',
            left: '50px',
            zIndex: 1000,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '5px 10px',
            cursor: 'pointer',
          }}
        >
          Fit to Data
        </button>
        <div
          style={{
            position: 'absolute',
            top: '10px',
            left: '150px',
            zIndex: 1000,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '5px 10px',
          }}
        >
          <strong>Total Captures:</strong>{' '}
          {totalCaptures !== null ? totalCaptures.toLocaleString() : 'N/A'}
        </div>
        {!isEmbedded && (
          <button
            onClick={handleShare}
            style={{
              position: 'absolute',
              top: '10px',
              right: '50px',
              zIndex: 1000,
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '5px 10px',
              cursor: 'pointer',
            }}
          >
            Share this Map
          </button>
        )}
        <button
          onClick={() => setShowBoundingBox(!showBoundingBox)}
          style={{
            position: 'absolute',
            top: '50px',
            left: '50px',
            zIndex: 1000,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '5px 10px',
            cursor: 'pointer',
          }}
        >
          {showBoundingBox ? 'Hide Bounding Box' : 'Show Bounding Box'}
        </button>
        {geoPolygon && (
          <>
            {geoPolygonFilterEnabled && (
              <button
                onClick={() => setShowGeoPolygon(!showGeoPolygon)}
                style={{
                  position: 'absolute',
                  top: '90px',
                  left: '50px',
                  zIndex: 1000,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  padding: '5px 10px',
                  cursor: 'pointer',
                }}
              >
                {showGeoPolygon ? 'Hide Geo-Polygon' : 'Show Geo-Polygon'}
              </button>
            )}
            {!isEmbedded && (
              <button
                onClick={() =>
                  setGeoPolygonFilterEnabled(!geoPolygonFilterEnabled)
                }
                style={{
                  position: 'absolute',
                  top: geoPolygonFilterEnabled ? '130px' : '90px',
                  left: '50px',
                  zIndex: 1000,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  padding: '5px 10px',
                  cursor: 'pointer',
                }}
              >
                {geoPolygonFilterEnabled
                  ? 'Disable Geo-Polygon Filter'
                  : 'Enable Geo-Polygon Filter'}
              </button>
            )}
          </>
        )}
        {isEmbedded && (
          <div
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 1000,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              padding: '2px 5px',
              borderRadius: '3px',
              fontSize: '0.7rem',
            }}
          >
            SigCap Map Visualization development funded in part by{' '}
            <a
              href="https://spectrumx.org"
              target="_blank"
              rel="noopener noreferrer"
            >
              SpectrumX Seed Award
            </a>
          </div>
        )}
      </div>
      <div className="my-3 mx-auto" style={{ maxWidth: '90%', flexShrink: 0 }}>
        <Row>
          {selectedNetworkType !== 'wifi' &&
            (showOperatorControl ? (
              <Col md="auto">
                <Form.Group>
                  <Form.Label>Operator</Form.Label>
                  <Dropdown
                    align="start"
                    onSelect={(eventKey) => {
                      setSelectedOperator(eventKey || 'all');
                    }}
                  >
                    <Dropdown.Toggle
                      variant="light"
                      className="w-100"
                      disabled={isSubmitted}
                    >
                      {selectedOperator}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item eventKey="all">all</Dropdown.Item>
                      {operators.map((operator) => (
                        <Dropdown.Item key={operator} eventKey={operator}>
                          {operator}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Form.Group>
              </Col>
            ) : (
              initialOperator && (
                <Col md="auto">
                  <Form.Label>Operator</Form.Label>
                  <div>
                    <strong>{initialOperator}</strong>
                  </div>
                </Col>
              )
            ))}
          {showTechnologyControl ? (
            <Col md="auto">
              <Form.Group>
                <Form.Label>Technology</Form.Label>
                <ButtonGroup className="w-100">
                  {getTechnologyOptions().map((tech) => (
                    <ToggleButton
                      key={tech.value}
                      id={`technology-${tech.value}-${uniqueId}`}
                      type="radio"
                      variant="outline-primary"
                      name={`technology-${uniqueId}`}
                      value={tech.value}
                      checked={technology === tech.value}
                      disabled={isSubmitted}
                      onChange={(e) => {
                        setTechnology(
                          e.currentTarget.value as typeof technology,
                        );
                      }}
                    >
                      {tech.label}
                    </ToggleButton>
                  ))}
                </ButtonGroup>
              </Form.Group>
            </Col>
          ) : (
            initialTechnology && (
              <Col md="auto">
                <Form.Label>Technology</Form.Label>
                <div>
                  <strong>
                    {
                      getTechnologyOptions().find(
                        (t) => t.value === initialTechnology,
                      )?.label
                    }
                  </strong>
                </div>
              </Col>
            )
          )}
          {showBandControl ? (
            <Col md="auto">
              <Form.Group>
                <Form.Label>Band</Form.Label>
                <Dropdown
                  align="start"
                  onSelect={(eventKey) => {
                    setBand(eventKey as typeof band);
                  }}
                >
                  <Dropdown.Toggle
                    variant="light"
                    className="w-100"
                    disabled={isSubmitted}
                  >
                    {band}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {getBandOptions().map((bandOption) => (
                      <Dropdown.Item key={bandOption} eventKey={bandOption}>
                        {bandOption}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>
            </Col>
          ) : (
            initialBand && (
              <Col md="auto">
                <Form.Label>Band</Form.Label>
                <div>
                  <strong>{initialBand}</strong>
                </div>
              </Col>
            )
          )}
          {showStatisticControl ? (
            <Col md="auto">
              <Form.Group>
                <Form.Label>Statistic</Form.Label>
                <ButtonGroup className="w-100">
                  <ToggleButton
                    id={`statistic-max-${uniqueId}`}
                    type="radio"
                    variant="outline-primary"
                    name={`statistic-${uniqueId}`}
                    value="max"
                    checked={statistic === 'max'}
                    disabled={isSubmitted}
                    onChange={(e) => {
                      setStatistic(e.currentTarget.value as 'max' | 'mean');
                    }}
                  >
                    Max
                  </ToggleButton>
                  <ToggleButton
                    id={`statistic-mean-${uniqueId}`}
                    type="radio"
                    variant="outline-primary"
                    name={`statistic-${uniqueId}`}
                    value="mean"
                    checked={statistic === 'mean'}
                    disabled={isSubmitted}
                    onChange={(e) => {
                      setStatistic(e.currentTarget.value as 'max' | 'mean');
                    }}
                  >
                    Mean
                  </ToggleButton>
                </ButtonGroup>
              </Form.Group>
            </Col>
          ) : (
            initialStatistic && (
              <Col md="auto">
                <Form.Label>Statistic</Form.Label>
                <div>
                  <strong>
                    {(initialStatistic || '').charAt(0).toUpperCase() +
                      (initialStatistic || '').slice(1)}
                  </strong>
                </div>
              </Col>
            )
          )}
          {showBinSizeControl ? (
            <Col md="auto">
              <Form.Group>
                <Form.Label>Bin size (m)</Form.Label>
                <Dropdown
                  align="start"
                  onSelect={(eventKey) => {
                    setBinSize(eventKey as typeof binSize);
                  }}
                >
                  <Dropdown.Toggle
                    variant="light"
                    className="w-100"
                    disabled={isSubmitted}
                  >
                    {binSize}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {avaiableBinSize.map((binSizeOption) => (
                      <Dropdown.Item
                        key={binSizeOption}
                        eventKey={binSizeOption}
                      >
                        {binSizeOption}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>
            </Col>
          ) : (
            initialBinSize && (
              <Col md="auto">
                <Form.Label>Bin size (m)</Form.Label>
                <div>
                  <strong>{initialBinSize}</strong>
                </div>
              </Col>
            )
          )}
          {showAnyControl && (
            <Col md="auto" className="d-flex align-items-end">
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSubmitted}
                className="btn btn-primary w-100"
                style={{ marginLeft: '1em' }}
              >
                {isSubmitted ? 'Loading...' : 'Submit'}
              </Button>
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
}
